import React, { Component } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Card,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Loader,
  TabsCustom,
  TabsCustomHeader,
  TabsCustomMenu,
  TabsCustomBody,
  TabsCustomItem,
} from "ui-kit-ck-consultant";

import {
  getCurrencyValue,
  getPercentageValue,
  getValue,
} from "../../../../utils/general";

import axiosConfig from "../../../../utils/axiosConfig";

const DELAY_LABELS = {
  delaiARMission: {
    title: "Délai Réception AR Mission",
    objective: "48h",
    max: 48,
    unit: "h",
  },
  delaiMissionAgenceLocale: {
    title: "Délai Mission → Mission agence locale",
    objective: "48h",
    max: 48,
    unit: "h",
  },
  delaiContactClient: {
    title: "Délai Mission → 1er Contact Client",
    objective: "72h",
    max: 72,
    unit: "h",
  },
  delaiMissionVisite: {
    title: "Délai Mission → Visite",
    objective: "40 jours",
    max: 40,
    unit: "j",
  },
  delaiVisiteRapport: {
    title: "Délai Visite → Rapport (dossiers clos)",
    objective: "20 jours",
    max: 20,
    unit: "j",
  },
  delaiMissionRapport: {
    title: "Délai Total Mission → Rapport (dossiers clos)",
    objective: "60 jours",
    max: 60,
    unit: "j",
  },
};

// Colonnes des listes de dossiers (modèle Excel "statistique ALLIANZ PJ")
const FOLDER_GROUPS = [
  { title: "Mission", span: 5 },
  { title: "Expertise", span: 2 },
  { title: "Rapport expertise et complément", span: 2 },
  { title: "Délais", span: 4 },
  { title: "Honoraires", span: 1 },
];

const FOLDER_COLUMNS = [
  { label: "Référence dossier expert", getVal: (f) => f.reference },
  { label: "Référence dossier client", getVal: (f) => f.sinister },
  { label: "Cabinet en charge", getVal: (f) => f.agency },
  {
    label: "Date réception mission",
    getVal: (f) => f.dateMissionHour || f.dateMission,
  },
  { label: "Etat de la mission", getVal: (f) => f.etat },
  { label: "Date réunion d'expertise", getVal: (f) => f.dateFirstRDV },
  { label: "Date dernière réunion", getVal: (f) => f.dateLastRDV },
  { label: "Date rapport expertise", getVal: (f) => f.dateClosing },
  { label: "Protocole d'accord", getVal: (f) => f.protocole },
  { label: "Délai 1 AR (h)", getVal: (f) => f.delaiARMissionH },
  { label: "Délai 2 M - V (j)", getVal: (f) => f.delaiMissionVisiteJ },
  { label: "Délai 3 V - R (j)", getVal: (f) => f.delaiVisiteRapportJ },
  { label: "Délai 4 M - R (j)", getVal: (f) => f.delaiTotalJ },
  { label: "Honoraires HT", getVal: (f) => f.honorairesHT },
];

export default class CardALLIANZPJ extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoad: false,
      data: null,
      tabsIndex: 0,
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate = (prevProps) => {
    if (
      JSON.stringify(prevProps.data) !== JSON.stringify(this.props.data) &&
      parseInt(this.props.data.company) === 40
    ) {
      this.getData();
    }
  };

  getData = () => {
    this.setState({ isLoad: true }, () => {
      axiosConfig("/company-performance/allianzPJ", {
        data: {
          year: this.props.data.dateEnd
            ? Number(moment(this.props.data.dateEnd).format("YYYY"))
            : Number(moment().format("YYYY")),
          network: this.props.data.network,
          group: this.props.data.group,
          region: this.props.data.region,
          agency: this.props.data.agency,
          company: this.props.data.company,
        },
      })
        .then((res) => {
          if (res.data.success) {
            this.setState({ data: res.data.data, isLoad: false });
          } else {
            this.setState({ isLoad: false, data: null });
          }
        })
        .catch(() => {
          this.setState({ isLoad: false });
        });
    });
  };

  buildStatsRows = (columns) => {
    const rows = [["", ...columns.map((col) => col.label)]];

    const addRow = (label, getVal) => {
      rows.push([
        label,
        ...columns.map((col) => (col.data ? getVal(col.data) : "")),
      ]);
    };
    const addSection = (title) => rows.push([title]);

    addRow("Nombre de dossiers reçus", (d) => d.dossiersRecus);
    addRow("Stock de dossiers en cours (total)", (d) => d.stockEnCours);
    addRow(
      "Age du stock de dossiers global",
      (d) => `${getValue(d.ageStockGlobal)} j`,
    );
    addRow("Stock de dossiers en cours à J+90", (d) => d.stockJ90);
    addRow("Nombre de dossiers clos", (d) => d.dossiersClos);

    addSection("Délais (dossiers reçus yc les clos)");
    Object.entries(DELAY_LABELS).forEach(([key, config]) => {
      addSection(config.title);
      rows.push(["OBJECTIF", ...columns.map(() => config.objective)]);
      addRow("Délai", (d) =>
        d[key] ? `${getValue(d[key].avgDelay)} ${config.unit}` : "",
      );
      addRow("% Dossiers ≤ délais", (d) =>
        d[key] ? getPercentageValue(d[key].pctWithin) : "",
      );
      addRow("% Dossiers > délais", (d) =>
        d[key] ? getPercentageValue(d[key].pctAbove) : "",
      );
      if (key === "delaiMissionRapport") {
        addRow("Nombre de dossiers ≤ délais", (d) =>
          d[key] ? d[key].withinObjective : "",
        );
        addRow("Nombre de dossiers > délais", (d) =>
          d[key] ? d[key].aboveObjective : "",
        );
      }
    });

    addSection("Protocoles d'accord / PV");
    addRow(
      "Nombre d'expertise en présence/représenté de la partie adverse",
      (d) => d.nbExpertiseAdverse,
    );
    addRow("Nombre de protocoles d'accord (dossiers clos)", (d) => d.nbProtocoles);
    addRow("% Dossiers protocole", (d) =>
      d.pctProtocoles !== null ? getPercentageValue(d.pctProtocoles) : "",
    );
    addRow("Nombre de PV (dossiers clos)", (d) => d.nbPV);
    addRow("% Dossiers PV", (d) =>
      d.pctPV !== null ? getPercentageValue(d.pctPV) : "",
    );
    addRow("Nombre d'issues amiables (dossiers clos)", (d) => d.nbAmiables);
    addRow("% Dossiers en issues amiables", (d) =>
      d.pctAmiables !== null ? getPercentageValue(d.pctAmiables) : "",
    );
    addRow("% de protocole sur les dossiers en issues amiables", (d) =>
      d.pctProtocolesAmiables !== null
        ? getPercentageValue(d.pctProtocolesAmiables)
        : "",
    );

    addSection("Qualité");
    addRow("Nombre de réclamations niveau 1 et 2", (d) => d.nbReclamations12);
    addRow("% Réponse > délais 72h", (d) =>
      d.pctReponseDelai72h !== null
        ? getPercentageValue(d.pctReponseDelai72h)
        : "",
    );
    addRow("Nombre de réclamation niveau 3", (d) => d.nbReclamations3);
    addRow("% Réponse > délais 48h", (d) =>
      d.pctReponseDelai48h !== null
        ? getPercentageValue(d.pctReponseDelai48h)
        : "",
    );
    addRow("% de décroche du centre relation client", (d) =>
      d.pctDecroche !== null ? getPercentageValue(d.pctDecroche) : "",
    );
    addRow("NPS : Objectif ≥ 4/5", (d) =>
      d.nps !== null ? getValue(d.nps) : "",
    );
    addRow("% de répondant à enquête qualité", (d) =>
      d.pctRepondantEnquete !== null
        ? getPercentageValue(d.pctRepondantEnquete)
        : "",
    );

    addSection("Honoraires");
    addRow("Honoraires TTC", (d) => getCurrencyValue(d.honorairesTTC));
    addRow("Honoraires Moyens TTC", (d) =>
      getCurrencyValue(d.honorairesMoyensTTC),
    );
    addRow("Honoraires TTC EAC", (d) =>
      d.honorairesTTCEAC !== null ? getCurrencyValue(d.honorairesTTCEAC) : "",
    );
    addRow("Honoraires TTC frais kilométriques", (d) =>
      d.honorairesTTCKm !== null ? getCurrencyValue(d.honorairesTTCKm) : "",
    );
    addRow("Dont impayés (%)", (d) =>
      d.pctImpayes !== null ? getPercentageValue(d.pctImpayes) : "",
    );

    return rows;
  };

  // Onglets dossiers au format du modèle Excel client : ligne de groupes
  // + ligne d'entêtes + une ligne par dossier
  buildFolderSheetRows = (folders) => {
    const groupRow = [];
    FOLDER_GROUPS.forEach((group) => {
      groupRow.push(group.title);
      for (let i = 1; i < group.span; i++) {
        groupRow.push(null);
      }
    });

    return [
      groupRow,
      FOLDER_COLUMNS.map((col) => col.label),
      ...folders.map((f) => FOLDER_COLUMNS.map((col) => col.getVal(f))),
    ];
  };

  handleExportXLSX = () => {
    const { data } = this.state;
    if (!data || !data.columns) return;

    const workBook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workBook,
      XLSX.utils.aoa_to_sheet(this.buildStatsRows(data.columns)),
      "Reporting",
    );
    XLSX.utils.book_append_sheet(
      workBook,
      XLSX.utils.aoa_to_sheet(this.buildFolderSheetRows(data.closedFolders || [])),
      "Dossiers clos",
    );
    XLSX.utils.book_append_sheet(
      workBook,
      XLSX.utils.aoa_to_sheet(this.buildFolderSheetRows(data.stockFolders || [])),
      "Dossiers en cours",
    );

    XLSX.writeFile(workBook, `statistiques_ALLIANZ_PJ_${data.year}.xlsx`);
  };

  renderTable = () => {
    const { data } = this.state;
    if (!data || !data.columns) return null;

    const columns = data.columns;

    return (
      <Card
        title="Statistiques ALLIANZ PJ"
        classNameChildren="overflow-x-auto"
        action={<FontAwesomeIcon icon={faFileExcel} title="Exporter en Excel" />}
        onClick={this.handleExportXLSX}
      >
        <Table>
          <Thead>
            <Tr>
              <Th></Th>
              {columns.map((col, idx) => (
                <Th key={idx}>{col.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {this.renderRow(
              "Nombre de dossiers reçus",
              columns,
              (d) => d.dossiersRecus,
              true,
            )}
            {this.renderRow(
              "Stock de dossiers en cours (total)",
              columns,
              (d) => d.stockEnCours,
              true,
            )}
            {this.renderRow(
              "Age du stock de dossiers global",
              columns,
              (d) => `${getValue(d.ageStockGlobal)} j`,
              true,
            )}
            {this.renderRow(
              "Stock de dossiers en cours à J+90",
              columns,
              (d) => d.stockJ90,
              true,
            )}
            {this.renderRow(
              "Nombre de dossiers clos",
              columns,
              (d) => d.dossiersClos,
              true,
            )}

            {this.renderSectionHeader(
              "Délais (dossiers reçus yc les clos)",
              columns,
            )}

            {Object.entries(DELAY_LABELS).map(([key, config]) =>
              this.renderDelayBlock(key, config, columns),
            )}

            {this.renderSectionHeader("Protocoles d'accord / PV", columns)}
            {this.renderRow(
              "Nombre d'expertise en présence/représenté de la partie adverse",
              columns,
              (d) => d.nbExpertiseAdverse,
            )}
            {this.renderRow(
              "Nombre de protocoles d'accord (dossiers clos)",
              columns,
              (d) => d.nbProtocoles,
            )}
            {this.renderRow(
              "% Dossiers protocole",
              columns,
              (d) => d.pctProtocoles !== null ? getPercentageValue(d.pctProtocoles) : "",
            )}
            {this.renderRow(
              "Nombre de PV (dossiers clos)",
              columns,
              (d) => d.nbPV,
            )}
            {this.renderRow(
              "% Dossiers PV",
              columns,
              (d) => d.pctPV !== null ? getPercentageValue(d.pctPV) : "",
            )}
            {this.renderRow(
              "Nombre d'issues amiables (dossiers clos)",
              columns,
              (d) => d.nbAmiables,
            )}
            {this.renderRow(
              "% Dossiers en issues amiables",
              columns,
              (d) => d.pctAmiables !== null ? getPercentageValue(d.pctAmiables) : "",
            )}
            {this.renderRow(
              "% de protocole sur les dossiers en issues amiables",
              columns,
              (d) => d.pctProtocolesAmiables !== null ? getPercentageValue(d.pctProtocolesAmiables) : "",
            )}

            {this.renderSectionHeader("Qualité", columns)}
            {this.renderRow(
              "Nombre de réclamations niveau 1 et 2",
              columns,
              (d) => d.nbReclamations12,
            )}
            {this.renderRow(
              "% Réponse > délais 72h",
              columns,
              (d) => d.pctReponseDelai72h !== null ? getPercentageValue(d.pctReponseDelai72h) : "",
            )}
            {this.renderRow(
              "Nombre de réclamation niveau 3",
              columns,
              (d) => d.nbReclamations3,
            )}
            {this.renderRow(
              "% Réponse > délais 48h",
              columns,
              (d) => d.pctReponseDelai48h !== null ? getPercentageValue(d.pctReponseDelai48h) : "",
            )}
            {this.renderRow(
              "% de décroche du centre relation client",
              columns,
              (d) => d.pctDecroche !== null ? getPercentageValue(d.pctDecroche) : "",
            )}
            {this.renderRow(
              "NPS : Objectif ≥ 4/5",
              columns,
              (d) => (d.nps !== null ? getValue(d.nps) : ""),
            )}
            {this.renderRow(
              "% de répondant à enquête qualité",
              columns,
              (d) => d.pctRepondantEnquete !== null ? getPercentageValue(d.pctRepondantEnquete) : "",
            )}

            {this.renderSectionHeader("Honoraires", columns)}
            {this.renderRow(
              "Honoraires TTC",
              columns,
              (d) => getCurrencyValue(d.honorairesTTC),
            )}
            {this.renderRow(
              "Honoraires Moyens TTC",
              columns,
              (d) => getCurrencyValue(d.honorairesMoyensTTC),
            )}
            {this.renderRow(
              "Honoraires TTC EAC",
              columns,
              (d) => d.honorairesTTCEAC !== null ? getCurrencyValue(d.honorairesTTCEAC) : "",
            )}
            {this.renderRow(
              "Honoraires TTC frais kilométriques",
              columns,
              (d) => d.honorairesTTCKm !== null ? getCurrencyValue(d.honorairesTTCKm) : "",
            )}
            {this.renderRow(
              "Dont impayés (%)",
              columns,
              (d) => d.pctImpayes !== null ? getPercentageValue(d.pctImpayes) : "",
            )}
          </Tbody>
        </Table>
      </Card>
    );
  };

  renderSectionHeader = (title, columns) => (
    <Tr key={`section-${title}`}>
      <Td
        className="fw-bold"
        colSpan={columns.length + 1}
        style={{ backgroundColor: "#FFC000" }}
      >
        {title}
      </Td>
    </Tr>
  );

  renderRow = (label, columns, getVal, isBold = false) => (
    <Tr key={label}>
      <Td
        className={isBold ? "fw-bold" : ""}
        style={{
          backgroundColor: isBold ? "#203764" : "#DDEBF7",
          color: isBold ? "#fff" : "#000",
        }}
      >
        {label}
      </Td>
      {columns.map((col, idx) => (
        <Td key={idx}>{col.data ? getVal(col.data) : ""}</Td>
      ))}
    </Tr>
  );

  renderDelayBlock = (key, config, columns) => {
    const rows = [];

    rows.push(
      <Tr key={`delay-title-${key}`}>
        <Td
          className="fw-bold"
          colSpan={columns.length + 1}
          style={{ backgroundColor: "#FFC000" }}
        >
          {config.title}
        </Td>
      </Tr>,
    );

    rows.push(
      <Tr key={`delay-obj-${key}`}>
        <Td className="fw-bold" style={{ backgroundColor: "#DDEBF7" }}>
          OBJECTIF
        </Td>
        {columns.map((_, idx) => (
          <Td
            key={idx}
            className="fw-bold"
            style={{ backgroundColor: "#DDEBF7" }}
          >
            {config.objective}
          </Td>
        ))}
      </Tr>,
    );

    rows.push(
      <Tr key={`delay-val-${key}`}>
        <Td style={{ backgroundColor: "#DDEBF7" }}>Délai</Td>
        {columns.map((col, idx) => {
          const delay = col.data ? col.data[key] : null;
          const avg = delay ? delay.avgDelay : null;
          let bg = null;
          if (avg !== null) {
            bg = avg <= config.max ? "#C6EFCE" : "#FFC7CE";
          }
          return (
            <Td key={idx} style={bg ? { backgroundColor: bg } : {}}>
              {avg !== null ? `${getValue(avg)} ${config.unit}` : ""}
            </Td>
          );
        })}
      </Tr>,
    );

    rows.push(
      this.renderRow(`% Dossiers ≤ délais`, columns, (d) => {
        const delay = d[key];
        return delay ? getPercentageValue(delay.pctWithin) : "";
      }),
    );

    rows.push(
      this.renderRow(`% Dossiers > délais`, columns, (d) => {
        const delay = d[key];
        return delay ? getPercentageValue(delay.pctAbove) : "";
      }),
    );

    if (key === "delaiMissionRapport") {
      rows.push(
        this.renderRow(`Nombre de dossiers ≤ délais`, columns, (d) => {
          const delay = d[key];
          return delay ? delay.withinObjective : "";
        }),
      );
      rows.push(
        this.renderRow(`Nombre de dossiers > délais`, columns, (d) => {
          const delay = d[key];
          return delay ? delay.aboveObjective : "";
        }),
      );
    }

    return rows;
  };

  renderFoldersTable = (isClosed) => {
    const { data } = this.state;
    if (!data) return null;

    const folders =
      (isClosed ? data.closedFolders : data.stockFolders) || [];

    return (
      <Card
        title={`${isClosed ? "Dossiers clos" : "Dossiers en cours"} (${folders.length})`}
        classNameChildren="overflow-x-auto"
        action={<FontAwesomeIcon icon={faFileExcel} title="Exporter en Excel" />}
        onClick={this.handleExportXLSX}
      >
        <Table>
          <Thead>
            <Tr>
              {FOLDER_GROUPS.map((group, idx) => (
                <Th key={idx} colSpan={group.span}>
                  {group.title}
                </Th>
              ))}
            </Tr>
            <Tr>
              {FOLDER_COLUMNS.map((col, idx) => (
                <Th key={idx}>{col.label}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {folders.map((folder, idx) => (
              <Tr key={idx}>
                {FOLDER_COLUMNS.map((col, colIdx) => (
                  <Td key={colIdx} className="nowrap">
                    {col.getVal(folder)}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>
    );
  };

  render() {
    return (
      <>
        <TabsCustom>
          <TabsCustomHeader>
            {["Statistiques", "Dossiers clos", "Dossiers en cours"].map(
              (title, idx) => (
                <TabsCustomMenu
                  key={idx}
                  title={title}
                  active={this.state.tabsIndex === idx}
                  onClick={() => this.setState({ tabsIndex: idx })}
                />
              ),
            )}
          </TabsCustomHeader>
          <TabsCustomBody noCard>
            <TabsCustomItem active={this.state.tabsIndex === 0}>
              {this.renderTable()}
            </TabsCustomItem>
            <TabsCustomItem active={this.state.tabsIndex === 1}>
              {this.renderFoldersTable(true)}
            </TabsCustomItem>
            <TabsCustomItem active={this.state.tabsIndex === 2}>
              {this.renderFoldersTable(false)}
            </TabsCustomItem>
          </TabsCustomBody>
        </TabsCustom>
        <Loader show={this.state.isLoad} />
      </>
    );
  }
}
